import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import Box from "@mui/material/Box";
import { DataGrid } from "@mui/x-data-grid";
import {
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  CircularProgress,
  TablePagination,
} from "@mui/material";
import PageHeader1 from "../../components/common/PageHeader1";
import axios from "axios";
import toast from "react-hot-toast";
import { getIntParam, setIfTruthy } from "../../utils/urlState";

function WalletTagesList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const didInitUrlSyncRef = React.useRef(false);

  const [paginationModel, setPaginationModel] = useState({
    page: getIntParam(searchParams, "page", 0),
    pageSize: getIntParam(searchParams, "pageSize", 10),
  });

  const [walletTags, setWalletTags] = useState([]);
  const [pagination, setPagination] = useState({ totalDocs: 0 });
  const [loading, setLoading] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTagId, setSelectedTagId] = useState(null);

  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});

  const BASE_URL = process.env.REACT_APP_BASEURL;
  const token = localStorage.getItem("astroVani-admin-token");

  const fetchWalletTags = async () => {
    setLoading(true);

    try {
      const params = new URLSearchParams();

      params.set("page", String(paginationModel.page + 1));
      params.set("limit", String(paginationModel.pageSize));

      const response = await axios.get(
        `${BASE_URL}/admin/rechargePackTag?${params.toString()}`,
        {
          headers: {
            Authorization: token,
          },
        }
      );

      const data = response.data;

      setWalletTags(data?.data || []);

      setPagination({
        totalDocs:
          data?.paginationDetail?.totalDocs ??
          data?.totalDocs ??
          0,
      });
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
        error?.message ||
        "Failed to load Wallet Tags."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWalletTags();
  }, [paginationModel.page, paginationModel.pageSize]);

  useEffect(() => {
    if (!didInitUrlSyncRef.current) {
      didInitUrlSyncRef.current = true;
      return;
    }

    const params = new URLSearchParams();

    setIfTruthy(params, "page", paginationModel.page);
    setIfTruthy(params, "pageSize", paginationModel.pageSize);

    setSearchParams(params, { replace: true });
  }, [
    paginationModel.page,
    paginationModel.pageSize,
    setSearchParams,
  ]);

  const handleClickOpen = (tagId) => {
    setSelectedTagId(tagId);
    setDeleteDialogOpen(true);
  };

  const handleClose = () => {
    setDeleteDialogOpen(false);
    setSelectedTagId(null);
  };

  const handleDelete = async () => {
    if (!selectedTagId) return;

    setLoading(true);

    try {
      await axios.delete(
        `${BASE_URL}/admin/rechargePackTag/${selectedTagId}`,
        {
          headers: {
            Authorization: token,
          },
        }
      );

      toast.success("Wallet Tag deleted successfully!");

      fetchWalletTags();
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
        error?.message ||
        "Failed to delete Wallet Tag."
      );
    } finally {
      setLoading(false);
      handleClose();
    }
  };

  const handleChangePage = (event, newPage) => {
    setPaginationModel((prev) => ({
      ...prev,
      page: newPage,
    }));
  };

  const handleChangeRowsPerPage = (event) => {
    const newPageSize = parseInt(event.target.value, 10) || 10;

    setPaginationModel({
      page: 0,
      pageSize: newPageSize,
    });
  };

  const columns = [
    {
      field: "sono",
      headerName: "S.No",
      width: 80,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 140,
      sortable: false,
      filterable: false,
      headerAlign: "center",
      align: "center",

      renderCell: ({ row }) => (
        <Box
          sx={{
            display: "flex",
            gap: 0.5,
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            height: "100%",
          }}
        >
          <IconButton
            size="small"
            component={Link}
            to={`/consultation-voucher-tag-edit/${row.id}`}
            title="Edit Wallet Tag"
            sx={{
              color: "#1976d2",
              "&:hover": {
                backgroundColor: "rgba(25,118,210,0.08)",
              },
            }}
          >
            <i
              className="icofont-edit"
              style={{ fontSize: "18px" }}
            ></i>
          </IconButton>

          <IconButton
            size="small"
            onClick={() => handleClickOpen(row.id)}
            title="Delete Wallet Tag"
            sx={{
              color: "#d32f2f",
              "&:hover": {
                backgroundColor: "rgba(211,47,47,0.08)",
              },
            }}
          >
            <i
              className="icofont-ui-delete"
              style={{ fontSize: "18px" }}
            ></i>
          </IconButton>
        </Box>
      ),
    },

    {
      field: "name",
      headerName: "Tag Name",
      width: 250,
    },

    {
      field: "isActive",
      headerName: "Status",
      width: 120,
      renderCell: ({ row }) => (
        <span
          className={`badge ${row.isActive ? "bg-success" : "bg-danger"
            }`}
        >
          {row.isActive ? "Active" : "Inactive"}
        </span>
      ),
    },

    {
      field: "createdAt",
      headerName: "Created At",
      width: 200,
      type: "dateTime",
    },

    {
      field: "updatedAt",
      headerName: "Updated At",
      width: 200,
      type: "dateTime",
    },
  ];

  const rows =
    walletTags?.map((tag, index) => {
      const pageSize = paginationModel.pageSize;

      return {
        id: tag?._id ?? index,
        sono:
          paginationModel.page * pageSize + index + 1,

        name: tag?.rechargePackTag ?? "N/A",

        isActive: tag?.isActive ?? false,

        createdAt: tag?.createdAt
          ? new Date(tag.createdAt)
          : null,

        updatedAt: tag?.updatedAt
          ? new Date(tag.updatedAt)
          : null,
      };
    }) || [];

  return (
    <div className="body d-flex">
      <div className="container-xxl">
        <PageHeader1
          righttitle="Add Wallet Tag"
          link="/consultation-voucher-tag-add"
          routebutton={true}
          pagetitle="Wallet Tags"
        />

        <div className="row g-0 mb-3">
          <div className="col-md-12">
            <div
              className="card"
              style={{
                borderRadius: "12px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                border: "none",
                overflow: "hidden",
              }}
            >
              <div
                className="card-body"
                style={{
                  padding: 0,
                  position: "relative",
                }}
              >
                <Box
                  sx={{
                    width: "100%",

                    "& .MuiDataGrid-root": {
                      border: "none",
                    },

                    "& .MuiDataGrid-cell": {
                      borderBottom: "1px solid #f0f0f0",
                    },

                    "& .MuiDataGrid-columnHeaders": {
                      backgroundColor: "#f8f9fa",
                      borderBottom: "2px solid #e9ecef",
                      fontWeight: 600,
                    },

                    "& .MuiDataGrid-footerContainer": {
                      display: "none",
                    },

                    "& .MuiDataGrid-row:hover": {
                      backgroundColor: "#f8f9fa",
                    },
                  }}
                >
                  <DataGrid
                    autoHeight
                    rows={rows}
                    columns={columns}
                    loading={loading}
                    paginationMode="server"
                    hideFooterPagination
                    disableSelectionOnClick
                    getRowId={(row) => row.id}
                    columnVisibilityModel={
                      columnVisibilityModel
                    }
                    onColumnVisibilityModelChange={(
                      newModel
                    ) =>
                      setColumnVisibilityModel(newModel)
                    }
                    sx={{
                      "& .MuiDataGrid-cell:focus": {
                        outline: "none",
                      },

                      "& .MuiDataGrid-cell:focus-within": {
                        outline: "none",
                      },
                    }}
                  />
                </Box>

                <TablePagination
                  component="div"
                  count={pagination?.totalDocs || 0}
                  page={paginationModel.page}
                  onPageChange={handleChangePage}
                  rowsPerPage={paginationModel.pageSize}
                  onRowsPerPageChange={
                    handleChangeRowsPerPage
                  }
                  rowsPerPageOptions={[10, 25, 50]}
                  labelRowsPerPage="Rows per page:"
                  sx={{
                    borderTop: "1px solid #e9ecef",

                    "& .MuiTablePagination-toolbar": {
                      padding: "12px 20px",
                    },
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleClose}
      >
        <DialogTitle>
          Delete Confirmation
        </DialogTitle>

        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this
            wallet tag?
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>
            Cancel
          </Button>

          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={20} />
            ) : (
              "Delete"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default WalletTagesList;